import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ZoomIn, ZoomOut, Maximize2, ArrowUp, ArrowRight, ArrowDown, ArrowLeft, Menu, Undo2, Redo2, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FlashingConfigurationForm, FlashingConfigurationFormRef } from './FlashingConfigurationForm';

export interface FlashingSegment {
  id: string;
  type: 'line' | 'bend';
  length?: number;
  direction?: number;
  angle?: number;
}

export interface FlashingConfig {
  segments: FlashingSegment[];
  variation: 'plastisol' | 'polyester' | 'galvanised_steel';
  plastisolPrice: number;
  polyesterPrice: number;
  galvanisedSteelPrice: number;
  quantity: number;
  weltStart?: boolean;
  weltEnd?: boolean;
  type?: 'internal' | 'external';
  color?: string;
  gauge?: '0.5mm' | '0.7mm' | '1.5mm';
}

interface FlashingBuilderProps {
  config?: FlashingConfig;
  onSave: (config: FlashingConfig) => void;
  onCancel: () => void;
  readOnlyPrices?: boolean;
}

const DEFAULT_PLASTISOL_PRICE = 60;
const DEFAULT_POLYESTER_PRICE = 50;
const DEFAULT_GALVANISED_STEEL_PRICE = 50;
const SHEET_WIDTH = 1220;
const SHEET_LENGTH = 3000;

const isMobile = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
         (typeof window !== 'undefined' && window.innerWidth < 768);
};

export function FlashingBuilder({ config, onSave, onCancel, readOnlyPrices = false }: FlashingBuilderProps) {
  // State
  const [segments, setSegments] = useState<FlashingSegment[]>(config?.segments || []);
  const [variation, setVariation] = useState<'plastisol' | 'polyester' | 'galvanised_steel'>(config?.variation || 'plastisol');
  const [plastisolPrice, setPlastisolPrice] = useState<number>(config?.plastisolPrice || DEFAULT_PLASTISOL_PRICE);
  const [polyesterPrice, setPolyesterPrice] = useState<number>(config?.polyesterPrice || DEFAULT_POLYESTER_PRICE);
  const [galvanisedSteelPrice, setGalvanisedSteelPrice] = useState<number>(config?.galvanisedSteelPrice || DEFAULT_GALVANISED_STEEL_PRICE);
  const [quantity, setQuantity] = useState<number>(config?.quantity || 1);
  const [weltStart, setWeltStart] = useState<boolean>(config?.weltStart || false);
  const [weltEnd, setWeltEnd] = useState<boolean>(config?.weltEnd || false);
  const [flashingType, setFlashingType] = useState<'internal' | 'external'>(config?.type || 'external');
  const [color, setColor] = useState<string>(config?.color || '');
  const [gauge, setGauge] = useState<'0.5mm' | '0.7mm' | '1.5mm'>(config?.gauge || '0.5mm');
  const [editingSegment, setEditingSegment] = useState<string | null>(null);
  const [tempLength, setTempLength] = useState<string>('');
  const [tempAngle, setTempAngle] = useState<string>('');
  const [tempDirection, setTempDirection] = useState<string>('');
  const [editingField, setEditingField] = useState<'length' | 'direction' | 'angle' | null>(null);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Canvas refs
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Input refs to maintain focus
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const plastisolPriceInputRef = useRef<HTMLInputElement>(null);
  const polyesterPriceInputRef = useRef<HTMLInputElement>(null);
  const galvanisedSteelPriceInputRef = useRef<HTMLInputElement>(null);
  
  // ViewBox state - Start with reasonable default that scales well
  const [viewBox, setViewBox] = useState({ x: -500, y: -500, width: 1000, height: 1000 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number; viewBox: typeof viewBox } | null>(null);
  const [touchState, setTouchState] = useState<{
    touches: Touch[];
    initialDistance: number;
    initialViewBox: typeof viewBox;
    centerPoint: { x: number; y: number };
    initialScreenCenter: { x: number; y: number };
  } | null>(null);
  const builderRootRef = useRef<HTMLDivElement>(null);
  
  // Drawing state
  const [drawingMode, setDrawingMode] = useState<'none' | 'line' | 'bend'>('none');
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [isContinuousDrawing, setIsContinuousDrawing] = useState<boolean>(true);
  const [hasDragged, setHasDragged] = useState<boolean>(false);
  const [spaceKeyHeld, setSpaceKeyHeld] = useState(false);
  const [drawStartPoint, setDrawStartPoint] = useState<{ x: number; y: number } | null>(null);
  const MIN_DRAG_DISTANCE = 10; // Minimum pixels to drag before starting to draw

  // Label hover state
  const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(null);

  // Mobile sheet state
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  // Snapping state
  const [snappingEnabled, setSnappingEnabled] = useState(true);
  const [shiftKeyHeld, setShiftKeyHeld] = useState(false);
  const [activeSnap, setActiveSnap] = useState<{
    type: 'angle' | 'grid' | 'both';
    snappedAngle?: number;
    guideLines: Array<{ x1: number; y1: number; x2: number; y2: number }>;
  } | null>(null);

  // Undo/Redo
  const [undoStack, setUndoStack] = useState<FlashingSegment[][]>([]);
  const [redoStack, setRedoStack] = useState<FlashingSegment[][]>([]);

  useEffect(() => {
    setIsMobileDevice(isMobile());
    const mql = window.matchMedia('(max-width: 767px)');
    const handleChange = () => {
      setIsMobileDevice(isMobile());
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Lock page scroll when flashing builder is active
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;

    const saved = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
    };

    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.height = '100%';
    (body.style as any).overscrollBehavior = 'none';
    (html.style as any).overscrollBehavior = 'none';

    return () => {
      html.style.overflow = saved.htmlOverflow;
      html.style.height = saved.htmlHeight;
      body.style.overflow = saved.bodyOverflow;
      body.style.position = saved.bodyPosition;
      body.style.top = saved.bodyTop;
      body.style.width = saved.bodyWidth;
      body.style.height = saved.bodyHeight;
      (body.style as any).overscrollBehavior = '';
      (html.style as any).overscrollBehavior = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Global touchmove prevention — blocks mobile scroll/pull-to-refresh
  useEffect(() => {
    const el = builderRootRef.current;
    if (!el) return;

    const prevent = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Allow scrolling inside dialogs, sheets, and sidebar
      if (target.closest('[role="dialog"]') ||
          target.closest('[data-sidebar="true"]')) {
        return;
      }
      e.preventDefault();
    };

    // { passive: false } is REQUIRED — Chrome defaults touchmove to passive
    el.addEventListener('touchmove', prevent, { passive: false });
    return () => el.removeEventListener('touchmove', prevent);
  }, []);

  // Calculate total width
  const totalWidth = useMemo(() => {
    const baseWidth = segments.reduce((sum, segment) => {
      if (segment.type === 'line' && segment.length) {
        return sum + segment.length;
      }
      return sum;
    }, 0);
    const weltWidth = (weltStart ? 15 : 0) + (weltEnd ? 15 : 0);
    return baseWidth + weltWidth;
  }, [segments, weltStart, weltEnd]);

  // Calculate units per sheet (accounting for offcuts)
  const unitsPerSheet = useMemo(() => {
    if (totalWidth === 0) return 0;
    return Math.floor(SHEET_WIDTH / totalWidth);
  }, [totalWidth]);

  // Calculate offcut per sheet
  const offcutPerSheet = useMemo(() => {
    if (totalWidth === 0 || unitsPerSheet === 0) return 0;
    return SHEET_WIDTH - (unitsPerSheet * totalWidth);
  }, [totalWidth, unitsPerSheet]);

  // Calculate price per unit (sheet price divided by units per sheet)
  const pricePerUnit = useMemo(() => {
    if (unitsPerSheet === 0) return 0;
    let pricePerSheet = 0;
    if (variation === 'plastisol') {
      pricePerSheet = plastisolPrice;
    } else if (variation === 'polyester') {
      pricePerSheet = polyesterPrice;
    } else if (variation === 'galvanised_steel') {
      pricePerSheet = galvanisedSteelPrice;
    }
    return pricePerSheet / unitsPerSheet;
  }, [unitsPerSheet, variation, plastisolPrice, polyesterPrice, galvanisedSteelPrice]);

  const calculatePrice = useCallback((): number => {
    if (unitsPerSheet === 0) return 0;
    return pricePerUnit * quantity;
  }, [pricePerUnit, quantity, unitsPerSheet]);

  const getCurrentDirection = useCallback((): number => {
    let currentAngle = 0;
    segments.forEach(segment => {
      if (segment.type === 'line' && segment.direction !== undefined) {
        currentAngle = segment.direction;
      } else if (segment.type === 'bend' && segment.angle !== undefined) {
        currentAngle = (currentAngle + segment.angle) % 360;
      }
    });
    return currentAngle;
  }, [segments]);

  const saveToHistory = useCallback((currentSegments: FlashingSegment[]) => {
    setUndoStack(prev => [...prev, [...currentSegments]]);
    setRedoStack([]);
  }, []);

  const normalizeAngle = useCallback((angle: number): number => {
    let normalized = angle % 360;
    if (normalized > 180) normalized -= 360;
    if (normalized < -180) normalized += 360;
    const absAngle = Math.abs(normalized);
    if (absAngle > 90) {
      return 180 - absAngle;
    }
    return absAngle;
  }, []);

  const calculatePathEndFromSegments = useCallback((segmentsToCalculate: FlashingSegment[]) => {
    let currentX = 0;
    let currentY = 0;
    let currentAngle = 0;
    
    segmentsToCalculate.forEach((segment) => {
      if (segment.type === 'line' && segment.length) {
        const direction = segment.direction !== undefined ? segment.direction : currentAngle;
        const dx = Math.cos((direction * Math.PI) / 180) * segment.length;
        const dy = Math.sin((direction * Math.PI) / 180) * segment.length;
        currentX += dx;
        currentY += dy;
        currentAngle = direction;
      } else if (segment.type === 'bend' && segment.angle !== undefined) {
        currentAngle = (currentAngle + segment.angle) % 360;
      }
    });
    
    return { x: currentX, y: currentY, angle: currentAngle };
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const previousState = undoStack[undoStack.length - 1];
    setRedoStack(prev => [[...segments], ...prev]);
    setUndoStack(prev => prev.slice(0, -1));
    setSegments(previousState);
    setDrawCurrent(null);
    setHasDragged(false);
    setDrawStart(null);
  }, [undoStack, segments]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[0];
    setUndoStack(prev => [...prev, [...segments]]);
    setRedoStack(prev => prev.slice(1));
    setSegments(nextState);
    setDrawCurrent(null);
    setHasDragged(false);
    setDrawStart(null);
  }, [redoStack, segments]);

  const updateSegment = useCallback((id: string, updates: Partial<FlashingSegment>) => {
    setSegments(prev => {
      saveToHistory(prev);
      return prev.map(s => s.id === id ? { ...s, ...updates } : s);
    });
  }, [saveToHistory]);

  const removeSegment = useCallback((id: string) => {
    if (segments.length === 1) return;
    setSegments(prev => {
      saveToHistory(prev);
      return prev.filter(s => s.id !== id);
    });
    if (editingSegment === id) {
      setEditingSegment(null);
      setEditingField(null);
    }
  }, [segments.length, editingSegment, saveToHistory]);

  const handleSaveLength = useCallback((id: string) => {
    const length = parseFloat(tempLength);
    if (!isNaN(length) && length >= 0) {
      updateSegment(id, { length });
    }
    setEditingField(null);
    setTempLength('');
  }, [tempLength, updateSegment]);

  const handleSaveDirection = useCallback((id: string) => {
    const direction = parseFloat(tempDirection);
    if (!isNaN(direction) && direction >= 0 && direction < 360) {
      updateSegment(id, { direction });
    }
    setEditingField(null);
    setTempDirection('');
  }, [tempDirection, updateSegment]);

  const handleSaveAngle = useCallback((id: string) => {
    const angle = parseFloat(tempAngle);
    if (!isNaN(angle) && angle >= -180 && angle <= 180) {
      setSegments(prev => {
        saveToHistory(prev);
        // Find the current segment and the next line segment
        const currentSegment = prev.find(s => s.id === id);
        const currentIndex = prev.findIndex(s => s.id === id);
        const nextLineSegment = prev.find((s, idx) => idx > currentIndex && s.type === 'line');
        const wasRidgePattern = currentSegment && Math.abs(currentSegment.angle || 0) === 135;
        
        // If user enters 135, keep it as 135 (ridge pattern)
        // If user enters 45 and it was originally 135, convert back to 135
        // Otherwise, use the entered value
        let angleToSave = Math.round(angle);
        let secondLineDirection: number | undefined = undefined;
        
        if (angle === 135) {
          angleToSave = 135; // Explicitly set to 135 for ridge pattern
          // For ridge pattern, update the second line direction to 135° to create visual 135° external angle
          if (nextLineSegment) {
            secondLineDirection = 135;
          }
        } else if (angle === 45 && wasRidgePattern) {
          angleToSave = 135; // Convert 45 back to 135 if it was originally a ridge pattern
          // Update second line direction to 135° for visual 135° external angle
          if (nextLineSegment) {
            secondLineDirection = 135;
          }
        }
        
        return prev.map(s => {
          if (s.id === id) {
            return { ...s, angle: angleToSave };
          } else if (secondLineDirection !== undefined && s.id === nextLineSegment?.id) {
            return { ...s, direction: secondLineDirection };
          }
          return s;
        });
      });
    }
    setEditingField(null);
    setEditingSegment(null);
    setTempAngle('');
  }, [tempAngle, saveToHistory]);

  const setDirection = useCallback((id: string, direction: number) => {
    updateSegment(id, { direction });
  }, [updateSegment]);

  const getDirectionLabel = useCallback((direction: number): string => {
    const normalized = ((direction % 360) + 360) % 360;
    if (normalized === 0 || Math.abs(normalized - 360) < 1) return 'East (0°)';
    if (Math.abs(normalized - 90) < 1) return 'South (90°)';
    if (Math.abs(normalized - 180) < 1) return 'West (180°)';
    if (Math.abs(normalized - 270) < 1) return 'North (270°)';
    return `${Math.round(normalized)}°`;
  }, []);

  const getDirectionIcon = useCallback((direction: number) => {
    const normalized = ((direction % 360) + 360) % 360;
    if (normalized >= 315 || normalized < 45) return <ArrowRight className="w-4 h-4" />;
    if (normalized >= 45 && normalized < 135) return <ArrowDown className="w-4 h-4" />;
    if (normalized >= 135 && normalized < 225) return <ArrowLeft className="w-4 h-4" />;
    return <ArrowUp className="w-4 h-4" />;
  }, []);

  const screenToSVG = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    if (!svgRef.current) return { x: 0, y: 0 };
    
    const svgRect = svgRef.current.getBoundingClientRect();
    if (svgRect.width <= 0 || svgRect.height <= 0 || viewBox.width <= 0 || viewBox.height <= 0) {
      return { x: viewBox.x, y: viewBox.y };
    }
    
    const x = clientX - svgRect.left;
    const y = clientY - svgRect.top;
    
    const viewBoxAspectRatio = viewBox.width / viewBox.height;
    const svgAspectRatio = svgRect.width / svgRect.height;
    
    let renderedWidth: number;
    let renderedHeight: number;
    let offsetX: number;
    let offsetY: number;
    
    if (viewBoxAspectRatio > svgAspectRatio) {
      renderedWidth = svgRect.width;
      renderedHeight = svgRect.width / viewBoxAspectRatio;
      offsetX = 0;
      offsetY = (svgRect.height - renderedHeight) / 2;
    } else {
      renderedWidth = svgRect.height * viewBoxAspectRatio;
      renderedHeight = svgRect.height;
      offsetX = (svgRect.width - renderedWidth) / 2;
      offsetY = 0;
    }
    
    if (renderedWidth <= 0 || renderedHeight <= 0 || !isFinite(renderedWidth) || !isFinite(renderedHeight)) {
      const svgX = viewBox.x + (x / svgRect.width) * viewBox.width;
      const svgY = viewBox.y + (y / svgRect.height) * viewBox.height;
      return { x: svgX, y: svgY };
    }
    
    const clampedX = Math.max(0, Math.min(svgRect.width, x));
    const clampedY = Math.max(0, Math.min(svgRect.height, y));
    const adjustedX = clampedX - offsetX;
    const adjustedY = clampedY - offsetY;
    const ratioX = Math.max(0, Math.min(1, adjustedX / renderedWidth));
    const ratioY = Math.max(0, Math.min(1, adjustedY / renderedHeight));
    
    const svgX = viewBox.x + ratioX * viewBox.width;
    const svgY = viewBox.y + ratioY * viewBox.height;
    
    if (!isFinite(svgX) || !isFinite(svgY)) {
      return { x: viewBox.x, y: viewBox.y };
    }
    
    return { x: svgX, y: svgY };
  }, [viewBox]);

  const getPathEndPoint = useCallback((): { x: number; y: number; angle: number } => {
    return calculatePathEndFromSegments(segments);
  }, [segments, calculatePathEndFromSegments]);

  // --- Snap utility functions ---
  const GRID_SIZE = 50;
  const ANGLE_SNAP_THRESHOLD = 5; // degrees
  const GRID_SNAP_THRESHOLD = 10; // mm
  const SHIFT_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
  const SNAP_ANGLES = Array.from({ length: 24 }, (_, i) => i * 15); // 0,15,30...345

  const snapAngle = useCallback((rawAngle: number, useShift: boolean): { angle: number; didSnap: boolean } => {
    if (!snappingEnabled && !useShift) return { angle: rawAngle, didSnap: false };
    const normalized = ((rawAngle % 360) + 360) % 360;
    const targets = useShift ? SHIFT_ANGLES : SNAP_ANGLES;
    let closest = normalized;
    let minDiff = Infinity;
    for (const t of targets) {
      let diff = Math.abs(normalized - t);
      if (diff > 180) diff = 360 - diff;
      const threshold = useShift ? 360 : ANGLE_SNAP_THRESHOLD;
      if (diff < minDiff && (useShift || diff <= threshold)) {
        minDiff = diff;
        closest = t;
      }
    }
    return { angle: closest, didSnap: closest !== normalized || useShift };
  }, [snappingEnabled]);

  const snapToGrid = useCallback((point: { x: number; y: number }): { snapped: { x: number; y: number }; didSnap: boolean } => {
    if (!snappingEnabled) return { snapped: point, didSnap: false };
    const gx = Math.round(point.x / GRID_SIZE) * GRID_SIZE;
    const gy = Math.round(point.y / GRID_SIZE) * GRID_SIZE;
    const dx = Math.abs(point.x - gx);
    const dy = Math.abs(point.y - gy);
    return {
      snapped: { x: dx <= GRID_SNAP_THRESHOLD ? gx : point.x, y: dy <= GRID_SNAP_THRESHOLD ? gy : point.y },
      didSnap: dx <= GRID_SNAP_THRESHOLD || dy <= GRID_SNAP_THRESHOLD,
    };
  }, [snappingEnabled]);

  const applySnapping = useCallback((
    start: { x: number; y: number },
    rawEnd: { x: number; y: number },
    useShift: boolean
  ) => {
    const dx = rawEnd.x - start.x;
    const dy = rawEnd.y - start.y;
    const rawAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
    const rawLength = Math.sqrt(dx * dx + dy * dy);

    // Snap angle
    const { angle: snappedAngle, didSnap: angleSnapped } = snapAngle(rawAngle, useShift);
    const rad = (snappedAngle * Math.PI) / 180;
    let endX = start.x + Math.cos(rad) * rawLength;
    let endY = start.y + Math.sin(rad) * rawLength;

    // Snap endpoint to grid
    const { snapped: gridEnd, didSnap: gridSnapped } = snapToGrid({ x: endX, y: endY });
    endX = gridEnd.x;
    endY = gridEnd.y;

    // Guide lines
    const guideLines: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    const guideExtent = 2000;
    if (angleSnapped) {
      guideLines.push({
        x1: start.x - Math.cos(rad) * guideExtent,
        y1: start.y - Math.sin(rad) * guideExtent,
        x2: start.x + Math.cos(rad) * guideExtent,
        y2: start.y + Math.sin(rad) * guideExtent,
      });
    }
    if (gridSnapped) {
      guideLines.push(
        { x1: endX - 15, y1: endY, x2: endX + 15, y2: endY },
        { x1: endX, y1: endY - 15, x2: endX, y2: endY + 15 }
      );
    }

    return {
      snappedEnd: { x: endX, y: endY },
      snappedAngle,
      length: Math.sqrt((endX - start.x) ** 2 + (endY - start.y) ** 2),
      angleSnapped,
      gridSnapped,
      guideLines,
    };
  }, [snapAngle, snapToGrid]);

  const cancelDrawing = useCallback(() => {
    setDrawStart(null);
    setDrawCurrent(null);
    setDrawStartPoint(null);
    setDrawingMode('none');
    setIsContinuousDrawing(false);
    setHasDragged(false);
    setActiveSnap(null);
  }, []);

  const calculatePath = useCallback(() => {
    const points: Array<{ x: number; y: number; angle: number; segmentId: string; type: string }> = [];
    let currentX = 0;
    let currentY = 0;
    let currentAngle = 0;
    
    points.push({ x: currentX, y: currentY, angle: currentAngle, segmentId: 'start', type: 'start' });

    segments.forEach((segment) => {
      if (segment.type === 'line' && segment.length) {
        const length = segment.length;
        const direction = segment.direction !== undefined ? segment.direction : currentAngle;
        const dx = Math.cos((direction * Math.PI) / 180) * length;
        const dy = Math.sin((direction * Math.PI) / 180) * length;
        currentX += dx;
        currentY += dy;
        currentAngle = direction;
        points.push({ x: currentX, y: currentY, angle: direction, segmentId: segment.id, type: 'line' });
      } else if (segment.type === 'bend' && segment.angle !== undefined) {
        currentAngle = (currentAngle + segment.angle) % 360;
        points.push({ x: currentX, y: currentY, angle: currentAngle, segmentId: segment.id, type: 'bend' });
      }
    });

    return points;
  }, [segments]);

  const handleZoomIn = useCallback(() => {
    setViewBox(prev => {
      const zoomFactor = 0.8;
      const newWidth = prev.width * zoomFactor;
      const newHeight = prev.height * zoomFactor;
      const centerX = prev.x + prev.width / 2;
      const centerY = prev.y + prev.height / 2;
      return {
        x: centerX - newWidth / 2,
        y: centerY - newHeight / 2,
        width: newWidth,
        height: newHeight
      };
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewBox(prev => {
      const zoomFactor = 1.25;
      const newWidth = prev.width * zoomFactor;
      const newHeight = prev.height * zoomFactor;
      const centerX = prev.x + prev.width / 2;
      const centerY = prev.y + prev.height / 2;
      return {
        x: centerX - newWidth / 2,
        y: centerY - newHeight / 2,
        width: Math.min(newWidth, 10000),
        height: Math.min(newHeight, 10000)
      };
    });
  }, []);

  const handleFitToView = useCallback(() => {
    const points = calculatePath();
    if (points.length === 0 || !containerRef.current || !svgRef.current) {
      setViewBox({ x: -500, y: -500, width: 1000, height: 1000 });
      return;
    }

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const drawingWidth = maxX - minX || 100;
    const drawingHeight = maxY - minY || 100;

    const svgRect = svgRef.current.getBoundingClientRect();

    // Proportional scaling: use consistent padding relative to drawing size
    // Small drawings get proportional padding so they don't fill the entire canvas
    const PADDING_FACTOR = 0.4; // 40% padding around the drawing
    const MIN_VIEWBOX = 300;    // Minimum viewbox dimension — prevents tiny drawings looking enormous

    const paddedWidth = drawingWidth * (1 + PADDING_FACTOR);
    const paddedHeight = drawingHeight * (1 + PADDING_FACTOR);

    // Scale to fit the SVG container while maintaining aspect ratio
    const aspectRatio = svgRect.width / svgRect.height;
    const scaleX = paddedWidth;
    const scaleY = paddedHeight * aspectRatio;
    const dominantDim = Math.max(scaleX, scaleY);

    // Calculate viewbox — enforce minimum so small flashings (20mm) don't appear enormous
    let newWidth = Math.max(dominantDim, MIN_VIEWBOX);
    let newHeight = newWidth / aspectRatio;

    // Ensure minimum height as well
    if (newHeight < MIN_VIEWBOX / aspectRatio) {
      newHeight = MIN_VIEWBOX / aspectRatio;
      newWidth = newHeight * aspectRatio;
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    setViewBox({
      x: centerX - newWidth / 2,
      y: centerY - newHeight / 2,
      width: newWidth,
      height: newHeight
    });
  }, [calculatePath]);

  // Auto-fit to view when segments change — skip during continuous drawing mode
  useEffect(() => {
    if (segments.length > 0 && !drawStart && !drawCurrent && drawingMode === 'none') {
      // Debounce the fit to avoid too many updates
      const timeoutId = setTimeout(() => {
        handleFitToView();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [segments.length, handleFitToView, drawStart, drawCurrent, drawingMode]);

  const getTouchDistance = (touch1: React.Touch | Touch, touch2: React.Touch | Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touch1: React.Touch | Touch, touch2: React.Touch | Touch): { x: number; y: number } => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const getEventPoint = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    if ('touches' in e && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY };
  };

  const handleStart = (e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    if (!containerRef.current || !svgRef.current) return;
    
    e.preventDefault();
    const point = getEventPoint(e);
    
    if ('touches' in e && e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      const center = getTouchCenter(touch1, touch2);
      
      if (!containerRef.current || !svgRef.current) return;
      const svgRect = svgRef.current.getBoundingClientRect();
      const centerX = center.x - svgRect.left;
      const centerY = center.y - svgRect.top;
      
      const svgCenterX = viewBox.x + (centerX / svgRect.width) * viewBox.width;
      const svgCenterY = viewBox.y + (centerY / svgRect.height) * viewBox.height;
      
      setTouchState({
        touches: Array.from(e.touches) as Touch[],
        initialDistance: distance,
        initialViewBox: { ...viewBox },
        centerPoint: { x: svgCenterX, y: svgCenterY },
        initialScreenCenter: { x: center.x, y: center.y }
      });
      setIsPanning(false);
        return;
      }
    
    if ('button' in e && e.button === 2) {
      setIsPanning(true);
      setPanStart({ x: point.x, y: point.y, viewBox: { ...viewBox } });
      return;
    }
    
    if (('button' in e && e.button === 1) || ('button' in e && e.button === 0 && spaceKeyHeld)) {
        setIsPanning(true);
      setPanStart({ x: point.x, y: point.y, viewBox: { ...viewBox } });
      return;
    }
    
    if (('button' in e && e.button === 0) || ('touches' in e && e.touches.length === 1)) {
      if (drawingMode === 'line' && drawStart) {
        // Continuous drawing: drawStart is already set from previous line's endpoint
        setIsPanning(false);
        setDrawStartPoint({ x: point.x, y: point.y }); // Fix: set for proper mousemove tracking
        const svgPoint = screenToSVG(point.x, point.y);
        setDrawCurrent(svgPoint);
        setHasDragged(true);
        return;
      } else if (drawingMode === 'line' && !drawStart) {
        // Store the initial click/touch point for drag detection
        setIsPanning(false);
        setDrawStartPoint({ x: point.x, y: point.y });
        const svgPoint = screenToSVG(point.x, point.y);
        if (isFinite(svgPoint.x) && isFinite(svgPoint.y)) {
          setDrawStart(svgPoint);
          setHasDragged(false);
        }
        return;
      }
      
      // Touch: single finger does NOT pan — use two fingers to pan+zoom
      // Mouse: pan when not in drawing mode or space is held
      if (!('touches' in e) && (drawingMode !== 'line' || spaceKeyHeld)) {
        setIsPanning(true);
        setPanStart({ x: point.x, y: point.y, viewBox: { ...viewBox } });
      }
    }
  };

  const handleMove = (e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    if (!containerRef.current || !svgRef.current) return;
    
    // Check if event is within canvas bounds - don't process sidebar events
    const containerRect = containerRef.current.getBoundingClientRect();
    const point = getEventPoint(e);
    
    // Early return if event is outside canvas bounds (likely in sidebar)
    if (point.x >= containerRect.right || point.x <= containerRect.left) {
      return;
    }
    
    // Don't prevent default for label interactions
    if ((e.target as HTMLElement)?.closest('[data-label]')) {
      return;
    }
    
    e.preventDefault();
    
    if ('touches' in e && e.touches.length === 2 && touchState && svgRef.current) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getTouchDistance(touch1, touch2);
      const scaleChange = touchState.initialDistance / distance;

      const svgRect = svgRef.current.getBoundingClientRect();
      const newWidth = touchState.initialViewBox.width * scaleChange;
      const newHeight = touchState.initialViewBox.height * scaleChange;

      // Current screen-space center of the two fingers
      const currentScreenCenter = getTouchCenter(touch1, touch2);

      // The initial SVG point under the fingers should stay under the fingers
      // This naturally handles both zoom AND pan simultaneously (like Maps)
      const newX = touchState.centerPoint.x
        - ((currentScreenCenter.x - svgRect.left) / svgRect.width) * newWidth;
      const newY = touchState.centerPoint.y
        - ((currentScreenCenter.y - svgRect.top) / svgRect.height) * newHeight;

      setViewBox({
        x: newX,
        y: newY,
        width: Math.max(50, Math.min(10000, newWidth)),
        height: Math.max(50, Math.min(10000, newHeight))
      });
      return;
    }
    
    if (drawingMode === 'line' && drawStart && drawStartPoint) {
      if (isPanning) {
        setIsPanning(false);
        setPanStart(null);
      }
      
      const point = getEventPoint(e);
      // Check if we've dragged far enough to start drawing
      const dragDistance = Math.sqrt(
        Math.pow(point.x - drawStartPoint.x, 2) + 
        Math.pow(point.y - drawStartPoint.y, 2)
      );
      
      if (dragDistance < MIN_DRAG_DISTANCE) {
        // Not enough drag, don't start drawing yet
        return;
      }
      
      const svgPoint = screenToSVG(point.x, point.y);
      if (isFinite(svgPoint.x) && isFinite(svgPoint.y) && drawStart) {
        if (snappingEnabled || shiftKeyHeld) {
          const snapResult = applySnapping(drawStart, svgPoint, shiftKeyHeld);
          setDrawCurrent(snapResult.snappedEnd);
          setActiveSnap({
            type: snapResult.angleSnapped && snapResult.gridSnapped ? 'both'
                  : snapResult.angleSnapped ? 'angle' : 'grid',
            snappedAngle: snapResult.snappedAngle,
            guideLines: snapResult.guideLines,
          });
        } else {
          setDrawCurrent(svgPoint);
          setActiveSnap(null);
        }
        setHasDragged(true);
      }
      return;
    }

    if (isPanning && panStart && svgRef.current) {
      const point = getEventPoint(e);
      const svgRect = svgRef.current.getBoundingClientRect();
      
      const deltaX = ((point.x - panStart.x) / svgRect.width) * panStart.viewBox.width;
      const deltaY = ((point.y - panStart.y) / svgRect.height) * panStart.viewBox.height;
      
      setViewBox({
        x: panStart.viewBox.x - deltaX,
        y: panStart.viewBox.y - deltaY,
        width: panStart.viewBox.width,
        height: panStart.viewBox.height
      });
    }
  };

  const handleEnd = (e: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
    if (!containerRef.current || !svgRef.current) return;
    
    // Check if event is within canvas bounds - don't process sidebar events
    const containerRect = containerRef.current.getBoundingClientRect();
    const point = getEventPoint(e);
    if (point.x > containerRect.right || point.x < containerRect.left) {
      // Reset drawing state if we're leaving the canvas
      if (drawingMode === 'line') {
        setDrawStart(null);
        setDrawCurrent(null);
      }
      setIsPanning(false);
      setPanStart(null);
      return;
    }
    
    // Don't prevent default for label interactions
    if ((e.target as HTMLElement)?.closest('[data-label]')) {
      return;
    }
    
    const isMouseLeave = e.type === 'mouseleave';
    const isTouchCancel = e.type === 'touchcancel';
    
    if (drawingMode === 'line' && drawStart && drawCurrent) {
      if (hasDragged && !isMouseLeave && !isTouchCancel) {
        if (!isFinite(drawCurrent.x) || !isFinite(drawCurrent.y) || 
            !isFinite(drawStart.x) || !isFinite(drawStart.y)) {
          setDrawCurrent(null);
          setHasDragged(false);
          return;
        }
        
      const dx = drawCurrent.x - drawStart.x;
      const dy = drawCurrent.y - drawStart.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      const direction = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
      
      // Minimum length of 20mm for lines
      const MIN_LINE_LENGTH = 20;
      if (length < MIN_LINE_LENGTH) {
        // Line too short, cancel drawing
        cancelDrawing();
        return;
      }
      
      const newSegments: FlashingSegment[] = [];
      const hasExistingSegments = segments.length > 0;
      
      // If we have existing segments, add a bend before the new line
      // This creates the pattern: Line → Bend → Line → Bend
      if (hasExistingSegments) {
        // Get the last line segment to calculate angle difference
        const lineSegments = segments.filter(s => s.type === 'line');
        const lastLine = lineSegments[lineSegments.length - 1];
        
        if (lastLine && lastLine.direction !== undefined) {
          // Calculate angle difference between last line and new line
          let angleDiff = direction - lastLine.direction;
          
          // Normalize angle to -180 to 180 range
          while (angleDiff > 180) angleDiff -= 360;
          while (angleDiff < -180) angleDiff += 360;
          
          // Always add a bend if there's any angle change (minimum 0.5 degree threshold for precision)
          if (Math.abs(angleDiff) >= 0.5) {
            newSegments.push({
              id: `bend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: 'bend',
              angle: Math.round(angleDiff * 10) / 10 // Round to 1 decimal place
            });
          }
        }
      }
      
      // Add the new line segment
      const newLine: FlashingSegment = {
        id: `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'line',
        length: Math.round(length * 10) / 10, // Round to 1 decimal place
        direction: Math.round(direction * 10) / 10 // Round to 1 decimal place
      };
      newSegments.push(newLine);
      
      // Update segments
      setSegments(prev => {
        saveToHistory(prev);
        return [...prev, ...newSegments];
      });
      
      setSelectedSegmentId(newLine.id);
      setActiveSnap(null);

      // Prepare for next line if continuous drawing
      if (isContinuousDrawing) {
        const newPathEnd = {
          x: drawStart.x + Math.cos((direction * Math.PI) / 180) * length,
          y: drawStart.y + Math.sin((direction * Math.PI) / 180) * length
        };
        setDrawStart(newPathEnd);
        setDrawCurrent(null);
        setHasDragged(false);
      } else {
        cancelDrawing();
      }
      } else if (isMouseLeave || isTouchCancel) {
        cancelDrawing();
      } else {
        // Click without drag, cancel if not enough movement
        if (drawStart && !hasDragged) {
          cancelDrawing();
        } else {
          setHasDragged(false);
        }
      }
    }
    
      setIsPanning(false);
    setPanStart(null);
    setTouchState(null);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Don't handle wheel events if configuration dialog is open
    if (showConfiguration) {
      return;
    }
    
    // Only prevent default if we're actually handling the zoom
    // Check if event is over the canvas, not over dialog or sidebar
    const target = e.target as HTMLElement;
    if (target.closest('[role="dialog"]')) {
      return; // Let dialog handle its own scrolling
    }
    
    // Check if the event is coming from the sidebar
    if (target.closest('[data-sidebar="true"]')) {
      return; // Let sidebar handle its own scrolling
    }
    
    // Prevent default to handle zoom
      e.preventDefault();
    e.stopPropagation();
    
    if (!svgRef.current) return;
    
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left;
    const mouseY = e.clientY - svgRect.top;
    
    const svgX = viewBox.x + (mouseX / svgRect.width) * viewBox.width;
    const svgY = viewBox.y + (mouseY / svgRect.height) * viewBox.height;
    
    const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
    const newWidth = viewBox.width * zoomFactor;
    const newHeight = viewBox.height * zoomFactor;
    
    const newX = svgX - (mouseX / svgRect.width) * newWidth;
    const newY = svgY - (mouseY / svgRect.height) * newHeight;
    
    setViewBox({
      x: newX,
      y: newY,
      width: Math.max(50, Math.min(10000, newWidth)),
      height: Math.max(50, Math.min(10000, newHeight))
    });
  };
  
  useEffect(() => {
    // Only attach keyboard handlers when dialog is NOT open
    if (showConfiguration) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in any input, textarea, or select
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement || 
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.closest('[role="combobox"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-form-field]') ||
        target.closest('[role="dialog"]')
      ) {
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y')) {
        return;
      }

      if (e.key === ' ') {
        setSpaceKeyHeld(true);
        e.preventDefault();
      }

      if (e.key === 'Shift') {
        setShiftKeyHeld(true);
      }

      if (e.key === 'Escape') {
        if (drawingMode === 'line') {
          cancelDrawing();
        }
        if (isPanning) {
          setIsPanning(false);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in any input, textarea, or select
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement || 
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.closest('[role="combobox"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-form-field]') ||
        target.closest('[role="dialog"]')
      ) {
        return;
      }
      
      if (e.key === ' ') {
        setSpaceKeyHeld(false);
        e.preventDefault();
      }

      if (e.key === 'Shift') {
        setShiftKeyHeld(false);
      }
    };

    // Use bubbling phase instead of capture to avoid interfering with inputs
    window.addEventListener('keydown', handleKeyDown, false);
    window.addEventListener('keyup', handleKeyUp, false);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, false);
      window.removeEventListener('keyup', handleKeyUp, false);
    };
  }, [drawingMode, isPanning, cancelDrawing, showConfiguration]);

  // Template functions
  const applyTemplate = (templateType: 'ridge' | 'barge', angle: 90 | 135, length: number) => {
    // For ridge templates with 135° external angle:
    // - Line 1: 0° (horizontal, pointing right)
    // - External angle: 135° means the second line should be at 135° direction
    // - This creates a visual 135° angle between the two lines
    // - We store the bend angle as 135° so the label shows correctly
    
    let storedAngle: number;
    let secondLineDirection: number;
    
    if (templateType === 'ridge' && angle === 135) {
      // For ridge: store 135° as the bend angle, and set second line to 135° to create visual 135° external angle
      storedAngle = 135;
      // Second line at 135° creates: visual angle = 135° - 0° = 135° (external angle)
      secondLineDirection = 135;
    } else {
      // For barge or other angles, use the angle directly
      storedAngle = angle;
      secondLineDirection = angle;
    }
    
    const templateSegments: FlashingSegment[] = [
      {
        id: `line-${Date.now()}-1`,
        type: 'line',
        length: length,
        direction: 0
      },
      {
        id: `bend-${Date.now()}`,
        type: 'bend',
        angle: storedAngle
      },
      {
        id: `line-${Date.now()}-2`,
        type: 'line',
        length: length,
        direction: secondLineDirection // This creates the visual 135° external angle for ridge patterns
      }
    ];
    
    saveToHistory(segments);
    setSegments(templateSegments);
    setDrawingMode('none');
    setDrawStart(null);
    setDrawCurrent(null);
    
    // Auto-fit to view after applying template
    setTimeout(() => {
      handleFitToView();
    }, 100);
  };

  useEffect(() => {
    // Don't auto-enable drawing mode on mount
    setDrawingMode('none');
    setIsContinuousDrawing(true);
  }, []);

  useEffect(() => {
    // Only attach keyboard handlers when dialog is NOT open
    if (showConfiguration) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in any input, textarea, or select
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement || 
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.closest('[role="combobox"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-form-field]') ||
        target.closest('[role="dialog"]')
      ) {
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    // Use bubbling phase instead of capture to avoid interfering with inputs
    document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown, false);
  }, [handleUndo, handleRedo, showConfiguration]);

  const handleLineClick = useCallback((segmentId: string, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (drawingMode === 'line') {
      cancelDrawing();
      return;
    }
    
    setSelectedSegmentId(segmentId);
  }, [drawingMode, cancelDrawing]);

  const openEditDialog = useCallback((segmentId: string, type: 'length' | 'angle') => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment) return;
    
    setEditingSegment(segmentId);
    setEditingField(type);
    if (type === 'length' && segment.type === 'line') {
      setTempLength(segment.length?.toString() || '');
    } else if (type === 'angle' && segment.type === 'bend') {
      // For ridge patterns (135°), use the original angle value instead of normalizing
      // This ensures the edit dialog shows 135° instead of 45°
      const absOriginalAngle = Math.abs(segment.angle || 0);
      const normalizedAngle = normalizeAngle(segment.angle || 0);
      
      // If this is a ridge pattern (135°), use 135° for editing
      // Otherwise use the normalized angle
      const isRidgePattern = absOriginalAngle === 135 || 
                            (normalizedAngle === 45 && absOriginalAngle === 135) ||
                            (normalizedAngle === 45 && absOriginalAngle > 90);
      
      const angleToEdit = isRidgePattern ? 135 : normalizedAngle;
      setTempAngle(angleToEdit.toString());
    }
  }, [segments, normalizeAngle]);

  // Render SVG
  const renderSVG = () => {
    const points = calculatePath();
    
    if (segments.length === 0 && drawingMode === 'none') {
      return (
        <div className="w-full h-full border rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 max-w-sm text-center px-4">
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600 dark:text-blue-400">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
            {/* Heading + description */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Design Your Flashing</h3>
              <p className="text-sm text-muted-foreground">Start by choosing a template or draw freehand</p>
            </div>
            {/* Quick action buttons */}
            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => applyTemplate('ridge', 135, 100)}
                className="h-9 px-4 text-xs font-medium"
              >
                Ridge 135°
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => applyTemplate('barge', 90, 100)}
                className="h-9 px-4 text-xs font-medium"
              >
                Barge 90°
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setDrawingMode('line');
                  setIsContinuousDrawing(true);
                  setDrawStart(null);
                  setDrawCurrent(null);
                  setDrawStartPoint(null);
                }}
                className="h-9 px-4 text-xs font-medium"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="mr-1.5">
                  <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                </svg>
                Draw Freehand
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const width = maxX - minX || 100;
    const height = maxY - minY || 100;
    // Adaptive scaling: labels and bend radius scale with the viewbox
    // so they remain readable at any zoom level
    const drawingScale = Math.max(0.5, Math.min(3, viewBox.width / 800));
    const baseSize = Math.max(width, height, 200);

    // Bend radius scales with drawing so it looks proportional
    const bendRadius = Math.max(15, baseSize * 0.06) * Math.min(1.5, drawingScale);

    let currentX = 0;
    let currentY = 0;
    let currentAngle = 0;

    const paths: JSX.Element[] = [];
    const labels: JSX.Element[] = [];
    const bendArcs: JSX.Element[] = [];
    const weltLines: JSX.Element[] = [];

    // Find first and last line segments for welt visualization
    const lineSegments = segments.filter(s => s.type === 'line');
    const firstLineSegmentId = lineSegments.length > 0 ? lineSegments[0].id : null;
    const lastLineSegmentId = lineSegments.length > 0 ? lineSegments[lineSegments.length - 1].id : null;
    
    // Track start and end points for welt visualization
    let firstLineStart: { x: number; y: number; direction: number } | null = null;
    let lastLineEnd: { x: number; y: number; direction: number } | null = null;

    // Track processed segment IDs to prevent duplicates
    const processedSegmentIds = new Set<string>();

    segments.forEach((segment) => {
      // Skip if this segment was already processed
      if (processedSegmentIds.has(segment.id)) {
        return;
      }
      processedSegmentIds.add(segment.id);
      if (segment.type === 'line' && segment.length) {
        const length = segment.length;
        const direction = segment.direction !== undefined ? segment.direction : currentAngle;
        const dx = Math.cos((direction * Math.PI) / 180) * length;
        const dy = Math.sin((direction * Math.PI) / 180) * length;
        
        const startX = currentX;
        const startY = currentY;
        const endX = currentX + dx;
        const endY = currentY + dy;

        // Track first and last line segments for welt visualization
        if (segment.id === firstLineSegmentId) {
          firstLineStart = { x: startX, y: startY, direction };
        }
        if (segment.id === lastLineSegmentId) {
          lastLineEnd = { x: endX, y: endY, direction };
        }

        const isSelected = selectedSegmentId === segment.id;
        
        paths.push(
          <g key={`line-group-${segment.id}`}
            onMouseEnter={() => setHoveredSegmentId(segment.id)}
            onMouseLeave={() => setHoveredSegmentId(null)}
          >
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke="transparent"
              strokeWidth={isMobileDevice ? "30" : "20"}
              strokeLinecap="round"
              className="cursor-pointer touch-none"
              onClick={(e) => {
                e.stopPropagation();
                openEditDialog(segment.id, 'length');
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                openEditDialog(segment.id, 'length');
              }}
              style={{ pointerEvents: 'stroke' }}
            />
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke={isSelected ? "#1d4ed8" : (hoveredSegmentId === segment.id ? "#2563eb" : "#3b82f6")}
              strokeWidth={isSelected ? "7" : (hoveredSegmentId === segment.id ? "6" : "5")}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-all cursor-pointer ${isSelected ? 'drop-shadow-lg' : ''}`}
            />
          </g>
        );

        // Position label close to the line - always horizontal for readability
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        const lineLength = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        
        // Calculate perpendicular offset (90 degrees from line direction) to place label above
        const perpAngle = (direction + 90) * Math.PI / 180;
        // Closer offset distance - labels should be near the line, scaled with drawing size
        // For small drawings, reduce offset to keep labels close
        const baseOffset = Math.max(15, Math.min(25, lineLength * 0.08));
        const offsetDistance = baseOffset * Math.max(0.6, drawingScale);
        
        // Calculate offset position above the line
        let labelOffsetX = Math.cos(perpAngle) * offsetDistance;
        let labelOffsetY = Math.sin(perpAngle) * offsetDistance;
        
        // Ensure label is always above the line (negative Y in SVG means "up")
        // If the perpendicular gives us downward direction, flip it
        if (labelOffsetY > 0) {
          labelOffsetX = -labelOffsetX;
          labelOffsetY = -labelOffsetY;
        }
        
        // Final label position - keep horizontal, don't rotate
        const labelX = midX + labelOffsetX;
        const labelY = midY + labelOffsetY;
        
        // Label dimensions — compact, semi-transparent, scale with viewbox (larger on mobile)
        const labelWidth = Math.max(isMobileDevice ? 48 : 36, (isMobileDevice ? 56 : 44) * drawingScale);
        const labelHeight = Math.max(isMobileDevice ? 16 : 12, (isMobileDevice ? 18 : 14) * drawingScale);
        const fontSize = Math.max(isMobileDevice ? 10 : 8, Math.min(isMobileDevice ? 13 : 11, (isMobileDevice ? 12 : 10) * drawingScale));
        const strokeWidth = Math.max(0.5, 0.8 * drawingScale);
        
        labels.push(
          <g
            key={`label-group-${segment.id}`}
            data-label="true"
            data-segment-id={segment.id}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              openEditDialog(segment.id, 'length');
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              openEditDialog(segment.id, 'length');
            }}
            onMouseEnter={() => setHoveredSegmentId(segment.id)}
            onMouseLeave={() => setHoveredSegmentId(null)}
            className="cursor-pointer touch-none"
            style={{ pointerEvents: 'all' }}
            opacity={hoveredSegmentId === segment.id || isSelected ? 1 : 0.4}
          >
            {/* Thin leader line from label to line midpoint */}
            <line
              x1={labelX} y1={labelY + labelHeight / 2}
              x2={midX} y2={midY}
              stroke="#9ca3af"
              strokeWidth={0.5 * drawingScale}
              strokeDasharray={`${2 * drawingScale},${2 * drawingScale}`}
              opacity={0.5}
              style={{ pointerEvents: 'none' }}
            />
            {/* Clean white background with subtle border - technical drawing style */}
            <rect
              x={labelX - labelWidth / 2}
              y={labelY - labelHeight / 2}
              width={labelWidth}
              height={labelHeight}
              rx={3 * drawingScale}
              fill="rgba(255, 255, 255, 0.75)"
              stroke={isSelected ? "#1d4ed8" : "#9ca3af"}
              strokeWidth={isSelected ? 2 * drawingScale : 1 * drawingScale}
              style={{
                pointerEvents: 'all',
                cursor: 'pointer'
              }}
            />
            <text
              x={labelX}
              y={labelY + (5 * drawingScale)}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#000000"
              fontWeight="normal"
              style={{
                pointerEvents: 'none',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >
              {segment.length.toFixed(0)}mm
            </text>
          </g>
        );

        currentX = endX;
        currentY = endY;
        currentAngle = direction;
      } else if (segment.type === 'bend' && segment.angle !== undefined) {
        const bendX = currentX;
        const bendY = currentY;
        const angle = segment.angle;
        const normalizedAngle = normalizeAngle(angle);
        const angleBefore = currentAngle;
        const angleAfter = (currentAngle + angle) % 360;
        
        // Find the next line segment to get its actual direction
        const currentIndex = segments.findIndex(s => s.id === segment.id);
        const nextLineSegment = segments.find((s, idx) => idx > currentIndex && s.type === 'line');
        const nextLineDirection = nextLineSegment?.direction !== undefined 
          ? nextLineSegment.direction 
          : angleAfter;
        
        // Calculate visual angle from actual line directions
        // For ridge patterns (135° external angle), calculate from line directions
        let visualAngle = Math.abs(nextLineDirection - angleBefore);
        if (visualAngle > 180) {
          visualAngle = 360 - visualAngle;
        }
        
        // Check if this is a ridge pattern based on stored angle or visual angle
        const absOriginalAngle = Math.abs(angle);
        const isRidgePattern = absOriginalAngle === 135 || 
                              (normalizedAngle === 45 && absOriginalAngle === 135) ||
                              (normalizedAngle === 45 && absOriginalAngle > 90) ||
                              (visualAngle === 135 && absOriginalAngle === 135);
        
        // For ridge patterns, use the visual angle (135°), otherwise use normalized angle
        const displayAngle = isRidgePattern ? 135 : normalizedAngle;
        
        const arcRadius = bendRadius;
        const startAngleRad = (angleBefore * Math.PI) / 180;
        const arcStartX = bendX - Math.cos(startAngleRad) * arcRadius;
        const arcStartY = bendY - Math.sin(startAngleRad) * arcRadius;
        
        const endAngleRad = (angleAfter * Math.PI) / 180;
        const arcEndX = bendX + Math.cos(endAngleRad) * arcRadius;
        const arcEndY = bendY + Math.sin(endAngleRad) * arcRadius;
        
        const interiorAngle = normalizedAngle;
        const midAngle = angleBefore + (angle / 2);
        const midAngleRad = (midAngle * Math.PI) / 180;
        const arcCenterX = bendX + Math.cos(midAngleRad + Math.PI / 2) * arcRadius;
        const arcCenterY = bendY + Math.sin(midAngleRad + Math.PI / 2) * arcRadius;
        
        const interiorAngleRad = (interiorAngle * Math.PI) / 180;
        const turnDirection = angle > 0 ? 1 : -1;
        
        let arcSpanAngle = absOriginalAngle;
        if (absOriginalAngle > 90) {
          arcSpanAngle = 180 - absOriginalAngle;
        }
        
        const arcSpanRad = (arcSpanAngle * Math.PI) / 180;
        const interiorEndAngleRad = startAngleRad + (turnDirection * arcSpanRad);
        const interiorArcEndX = bendX - Math.cos(interiorEndAngleRad) * arcRadius;
        const interiorArcEndY = bendY - Math.sin(interiorEndAngleRad) * arcRadius;
        
        // Remove the yellow arc line - user requested removal
        // bendArcs.push(...) removed

        paths.push(
          <circle
            key={`bend-circle-${segment.id}`}
            cx={bendX}
            cy={bendY}
            r={Math.max(5, arcRadius * 0.3)}
            fill="#fbbf24"
            stroke="#f59e0b"
            strokeWidth="1.5"
            className="hover:fill-amber-500 transition-colors"
            opacity="0.8"
          />
        );

        // Position angle label directly centered on the yellow dot
        const labelX = bendX;
        const labelY = bendY;
        const isSelected = selectedSegmentId === segment.id;
        
        // Angle label dimensions — scale with viewbox (larger on mobile)
        const angleLabelWidth = Math.max(isMobileDevice ? 40 : 30, (isMobileDevice ? 46 : 36) * drawingScale);
        const angleLabelHeight = Math.max(isMobileDevice ? 16 : 12, (isMobileDevice ? 18 : 14) * drawingScale);
        const angleFontSize = Math.max(isMobileDevice ? 10 : 8, Math.min(isMobileDevice ? 13 : 11, (isMobileDevice ? 12 : 10) * drawingScale));
        const angleStrokeWidth = Math.max(0.5, 0.8 * drawingScale);
        
        labels.push(
          <g
            key={`bend-label-group-${segment.id}`}
            data-label="true"
            onClick={(e) => {
              e.stopPropagation();
              openEditDialog(segment.id, 'angle');
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              openEditDialog(segment.id, 'angle');
            }}
            onMouseEnter={() => setHoveredSegmentId(segment.id)}
            onMouseLeave={() => setHoveredSegmentId(null)}
            className="cursor-pointer touch-none"
            style={{ pointerEvents: 'all' }}
            opacity={hoveredSegmentId === segment.id || isSelected ? 1 : 0.4}
          >
            {/* Clean white background with subtle border - technical drawing style */}
            <rect
              x={labelX - angleLabelWidth / 2}
              y={labelY - angleLabelHeight / 2}
              width={angleLabelWidth}
              height={angleLabelHeight}
              rx={3 * drawingScale}
              fill="rgba(255, 255, 255, 0.75)"
              stroke={isSelected ? "#1d4ed8" : "#9ca3af"}
              strokeWidth={isSelected ? 2 * drawingScale : 1 * drawingScale}
              style={{ 
                pointerEvents: 'all', 
                cursor: 'pointer'
              }}
            />
            {/* Angle text - clear and readable */}
            <text
              x={labelX}
              y={labelY + (5 * drawingScale)}
              textAnchor="middle"
              fontSize={angleFontSize}
              fill="#000000"
              fontWeight="normal"
              className="pointer-events-none"
              style={{ 
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >
              {Math.round(displayAngle)}°
            </text>
          </g>
        );

        currentAngle = angleAfter;
      }
    });

    // Draw welt lines if enabled
    if (weltStart && firstLineStart) {
      // Draw red welt line at start, extending backwards 15mm
      const weltDirectionRad = ((firstLineStart.direction + 180) * Math.PI) / 180; // Opposite direction
      const weltEndX = firstLineStart.x + Math.cos(weltDirectionRad) * 15;
      const weltEndY = firstLineStart.y + Math.sin(weltDirectionRad) * 15;
      const labelX = firstLineStart.x + Math.cos(weltDirectionRad) * 7.5;
      const labelY = firstLineStart.y + Math.sin(weltDirectionRad) * 7.5;
      
      weltLines.push(
        <g key="welt-start">
          {/* Thick background line for visibility */}
          <line
            x1={firstLineStart.x}
            y1={firstLineStart.y}
            x2={weltEndX}
            y2={weltEndY}
            stroke="#ffffff"
            strokeWidth="10"
            strokeLinecap="round"
            opacity="0.8"
          />
          {/* Main red welt line */}
          <line
            x1={firstLineStart.x}
            y1={firstLineStart.y}
            x2={weltEndX}
            y2={weltEndY}
            stroke="#dc2626"
            strokeWidth="7"
            strokeLinecap="round"
            opacity="1"
          />
          {/* Label background */}
          <rect
            x={labelX - 25}
            y={labelY - 18}
            width="50"
            height="20"
            rx="4"
            fill="#ffffff"
            stroke="#dc2626"
            strokeWidth="2"
            opacity="0.95"
          />
          {/* Label text */}
          <text
            x={labelX}
            y={labelY - 3}
            textAnchor="middle"
            fontSize="12"
            fill="#dc2626"
            fontWeight="bold"
            className="pointer-events-none"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            WELT
          </text>
        </g>
      );
    }

    if (weltEnd && lastLineEnd) {
      // Draw red welt line at end, extending forwards 15mm
      const weltDirectionRad = (lastLineEnd.direction * Math.PI) / 180;
      const weltEndX = lastLineEnd.x + Math.cos(weltDirectionRad) * 15;
      const weltEndY = lastLineEnd.y + Math.sin(weltDirectionRad) * 15;
      const labelX = lastLineEnd.x + Math.cos(weltDirectionRad) * 7.5;
      const labelY = lastLineEnd.y + Math.sin(weltDirectionRad) * 7.5;
      
      weltLines.push(
        <g key="welt-end">
          {/* Thick background line for visibility */}
          <line
            x1={lastLineEnd.x}
            y1={lastLineEnd.y}
            x2={weltEndX}
            y2={weltEndY}
            stroke="#ffffff"
            strokeWidth="10"
            strokeLinecap="round"
            opacity="0.8"
          />
          {/* Main red welt line */}
          <line
            x1={lastLineEnd.x}
            y1={lastLineEnd.y}
            x2={weltEndX}
            y2={weltEndY}
            stroke="#dc2626"
            strokeWidth="7"
            strokeLinecap="round"
            opacity="1"
          />
          {/* Label background */}
          <rect
            x={labelX - 25}
            y={labelY - 18}
            width="50"
            height="20"
            rx="4"
            fill="#ffffff"
            stroke="#dc2626"
            strokeWidth="2"
            opacity="0.95"
          />
          {/* Label text */}
          <text
            x={labelX}
            y={labelY - 3}
            textAnchor="middle"
            fontSize="12"
            fill="#dc2626"
            fontWeight="bold"
            className="pointer-events-none"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            WELT
          </text>
        </g>
      );
    }

    return (
      <div
        className="w-full h-full border rounded-lg bg-gray-50 dark:bg-gray-900 overflow-hidden relative"
        style={{
          cursor: isPanning
            ? 'grabbing'
            : spaceKeyHeld
              ? 'grab'
              : drawingMode === 'line'
                ? 'crosshair'
                : 'default',
          touchAction: showConfiguration ? 'auto' : 'none',
          overscrollBehavior: 'contain',
        }}
      >
        <svg
          ref={svgRef}
          className="w-full h-full"
          viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
          preserveAspectRatio="xMidYMid meet"
          shapeRendering="geometricPrecision"
          style={{ 
            display: 'block',
            touchAction: showConfiguration ? 'auto' : 'none',
            pointerEvents: showConfiguration ? 'none' : 'auto'
          }}
          onMouseDown={showConfiguration ? undefined : handleStart}
          onMouseMove={showConfiguration ? undefined : handleMove}
          onMouseUp={showConfiguration ? undefined : handleEnd}
          onMouseLeave={showConfiguration ? undefined : handleEnd}
          onTouchStart={showConfiguration ? undefined : (e) => {
            const containerRect = containerRef.current?.getBoundingClientRect();
            if (!containerRect || !e.touches[0]) return;
            // Only process if within canvas container bounds
            if (e.touches[0].clientX < containerRect.right && e.touches[0].clientX >= containerRect.left) {
              handleStart(e);
            }
          }}
          onTouchMove={showConfiguration ? undefined : (e) => {
            const containerRect = containerRef.current?.getBoundingClientRect();
            if (!containerRect || !e.touches[0]) return;
            // Only process if within canvas container bounds
            if (e.touches[0].clientX < containerRect.right && e.touches[0].clientX >= containerRect.left) {
              handleMove(e);
            }
          }}
          onTouchEnd={showConfiguration ? undefined : (e) => {
            const containerRect = containerRef.current?.getBoundingClientRect();
            if (!containerRect || !e.changedTouches[0]) return;
            // Only process if within canvas container bounds
            if (e.changedTouches[0].clientX < containerRect.right && e.changedTouches[0].clientX >= containerRect.left) {
              handleEnd(e);
            }
          }}
          onTouchCancel={showConfiguration ? undefined : handleEnd}
          onContextMenu={(e) => {
            e.preventDefault();
          }}
        >
          <defs>
            <filter id="shadow">
              <feDropShadow dx="1" dy="1" stdDeviation="2" floodOpacity="0.3"/>
            </filter>
            {/* Minor grid: 50mm spacing, thin light lines */}
            <pattern id="grid-minor" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
            </pattern>
            {/* Major grid: 250mm spacing, thicker lines */}
            <pattern id="grid-major" width="250" height="250" patternUnits="userSpaceOnUse">
              <path d="M 250 0 L 0 0 0 250" fill="none" stroke="#d1d5db" strokeWidth="1.2"/>
            </pattern>
          </defs>

          <rect x="-10000" y="-10000" width="20000" height="20000" fill="url(#grid-minor)" />
          <rect x="-10000" y="-10000" width="20000" height="20000" fill="url(#grid-major)" />
            
            {points.length > 0 && (
              <circle
                cx={points[0].x}
                cy={points[0].y}
                r="8"
                fill="#3b82f6"
                stroke="#1e40af"
                strokeWidth="2.5"
              />
            )}
            
            {paths}
            {weltLines}
            {labels}

            {/* Scale indicator — dynamic ruler in bottom-left */}
            {(() => {
              const scaleBarPixels = 100;
              const svgRectScale = svgRef.current?.getBoundingClientRect();
              if (!svgRectScale || svgRectScale.width <= 0) return null;

              const unitsPerPixel = viewBox.width / svgRectScale.width;
              const rawLength = scaleBarPixels * unitsPerPixel;

              const niceNumbers = [10, 20, 25, 50, 100, 200, 250, 500, 1000];
              const niceLength = niceNumbers.reduce((prev, curr) =>
                Math.abs(curr - rawLength) < Math.abs(prev - rawLength) ? curr : prev
              );

              const barWidthSvg = niceLength;
              const barX = viewBox.x + viewBox.width * 0.03;
              const barY = viewBox.y + viewBox.height * 0.93;
              const tickH = viewBox.height * 0.015;
              const barThick = viewBox.width * 0.003;
              const fs = viewBox.width * 0.016;

              return (
                <g opacity="0.65" className="pointer-events-none">
                  <rect
                    x={barX - fs * 0.4}
                    y={barY - tickH - fs * 1.6}
                    width={barWidthSvg + fs * 0.8}
                    height={tickH * 2 + fs * 2.2}
                    rx={fs * 0.3}
                    fill="rgba(255,255,255,0.88)"
                    stroke="#aaa"
                    strokeWidth={viewBox.width * 0.0008}
                  />
                  <line x1={barX} y1={barY} x2={barX + barWidthSvg} y2={barY}
                    stroke="#444" strokeWidth={barThick} />
                  <line x1={barX} y1={barY - tickH} x2={barX} y2={barY + tickH}
                    stroke="#444" strokeWidth={barThick * 0.6} />
                  <line x1={barX + barWidthSvg} y1={barY - tickH}
                    x2={barX + barWidthSvg} y2={barY + tickH}
                    stroke="#444" strokeWidth={barThick * 0.6} />
                  <text
                    x={barX + barWidthSvg / 2}
                    y={barY - tickH - fs * 0.3}
                    textAnchor="middle"
                    fontSize={fs}
                    fill="#444"
                    style={{ fontFamily: 'system-ui, sans-serif', fontWeight: 500 }}
                  >
                    {niceLength >= 1000 ? `${niceLength / 1000}m` : `${niceLength}mm`}
                  </text>
                </g>
              );
            })()}

            {drawingMode === 'line' && drawStart && !drawCurrent && (
              <g>
                <circle
                  cx={drawStart.x}
                  cy={drawStart.y}
                  r="8"
                  fill="#8b5cf6"
                  stroke="#7c3aed"
                  strokeWidth="3"
                  opacity="0.9"
                  className="animate-pulse"
                />
                <circle
                  cx={drawStart.x}
                  cy={drawStart.y}
                  r="12"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                  opacity="0.5"
                  className="animate-ping"
                />
                <text
                  x={drawStart.x}
                  y={drawStart.y - 20}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#7c3aed"
                  className="font-bold pointer-events-none"
                >
                  Start Point
                </text>
              </g>
            )}
            
            {/* Snap guide lines */}
            {activeSnap?.guideLines && activeSnap.guideLines.map((gl, i) => (
              <line
                key={`snap-guide-${i}`}
                x1={gl.x1} y1={gl.y1} x2={gl.x2} y2={gl.y2}
                stroke="#22c55e"
                strokeWidth={1 * drawingScale}
                strokeDasharray={`${4 * drawingScale},${4 * drawingScale}`}
                opacity={0.4}
                className="pointer-events-none"
              />
            ))}

            {drawingMode === 'line' && drawStart && drawCurrent && (
              <g>
                {/* Protractor arc — shows angle reference from 0° */}
                {(() => {
                  const pdx = drawCurrent.x - drawStart.x;
                  const pdy = drawCurrent.y - drawStart.y;
                  const pLen = Math.sqrt(pdx * pdx + pdy * pdy);
                  if (pLen < 5) return null;
                  const arcR = Math.min(30 * drawingScale, pLen * 0.4);
                  const rawDeg = (Math.atan2(pdy, pdx) * 180 / Math.PI + 360) % 360;
                  // Reference line along 0° (horizontal right)
                  const refEndX = drawStart.x + arcR;
                  const refEndY = drawStart.y;
                  // Arc endpoint
                  const arcEndX = drawStart.x + Math.cos(rawDeg * Math.PI / 180) * arcR;
                  const arcEndY = drawStart.y + Math.sin(rawDeg * Math.PI / 180) * arcR;
                  const largeArc = rawDeg > 180 ? 1 : 0;
                  const sweepFlag = 1;
                  const arcColor = activeSnap ? "rgba(34,197,94,0.3)" : "rgba(139,92,246,0.25)";
                  const refColor = activeSnap ? "rgba(34,197,94,0.2)" : "rgba(139,92,246,0.15)";
                  return (
                    <g className="pointer-events-none">
                      {/* 0° reference line */}
                      <line x1={drawStart.x} y1={drawStart.y} x2={refEndX} y2={refEndY}
                        stroke={refColor} strokeWidth={1 * drawingScale}
                        strokeDasharray={`${3 * drawingScale},${3 * drawingScale}`} />
                      {/* Arc from 0° to current angle */}
                      <path
                        d={`M ${refEndX} ${refEndY} A ${arcR} ${arcR} 0 ${largeArc} ${sweepFlag} ${arcEndX} ${arcEndY}`}
                        fill="none" stroke={arcColor} strokeWidth={1.5 * drawingScale} />
                    </g>
                  );
                })()}
                {/* Start point */}
                <circle cx={drawStart.x} cy={drawStart.y} r={6 * drawingScale}
                  fill={activeSnap ? "#22c55e" : "#8b5cf6"} stroke={activeSnap ? "#16a34a" : "#7c3aed"}
                  strokeWidth={2 * drawingScale} opacity="0.8" />
                {/* Shadow line for depth */}
                <line x1={drawStart.x} y1={drawStart.y} x2={drawCurrent.x} y2={drawCurrent.y}
                  stroke="rgba(0,0,0,0.08)" strokeWidth={6 * drawingScale} strokeLinecap="round"
                  className="pointer-events-none" />
                {/* Preview line - solid, colored by snap state */}
                <line x1={drawStart.x} y1={drawStart.y} x2={drawCurrent.x} y2={drawCurrent.y}
                  stroke={activeSnap ? "#22c55e" : "#8b5cf6"} strokeWidth={3 * drawingScale}
                  strokeLinecap="round" opacity="0.85" className="pointer-events-none" />
                {/* End point */}
                <circle cx={drawCurrent.x} cy={drawCurrent.y} r={5 * drawingScale}
                  fill={activeSnap ? "#22c55e" : "#8b5cf6"} stroke={activeSnap ? "#16a34a" : "#7c3aed"}
                  strokeWidth={2 * drawingScale} opacity="0.85" />
                {/* Snap indicator dot */}
                {activeSnap && (
                  <circle cx={drawCurrent.x} cy={drawCurrent.y} r={8 * drawingScale}
                    fill="none" stroke="#22c55e" strokeWidth={1.5 * drawingScale}
                    strokeDasharray={`${3 * drawingScale},${3 * drawingScale}`} opacity="0.6"
                    className="pointer-events-none" />
                )}
                {(() => {
                  const dx = drawCurrent.x - drawStart.x;
                  const dy = drawCurrent.y - drawStart.y;
                  const length = Math.sqrt(dx * dx + dy * dy);
                  const direction = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
                  const midX = (drawStart.x + drawCurrent.x) / 2;
                  const midY = (drawStart.y + drawCurrent.y) / 2;

                  const labelAngle = direction + 90;
                  const lo = 25 * drawingScale;
                  const labelOffsetX = Math.cos((labelAngle * Math.PI) / 180) * lo;
                  const labelOffsetY = Math.sin((labelAngle * Math.PI) / 180) * lo;
                  const isSnapped = !!activeSnap;
                  const bgColor = isSnapped ? "rgba(34, 197, 94, 0.95)" : "rgba(139, 92, 246, 0.95)";
                  const borderColor = isSnapped ? "#16a34a" : "#7c3aed";
                  const lw = 70 * drawingScale;
                  const lh = 24 * drawingScale;
                  const fs1 = 12 * drawingScale;
                  const fs2 = 10 * drawingScale;

                  return (
                    <g>
                      <rect x={midX + labelOffsetX - lw/2} y={midY + labelOffsetY - lh/2}
                        width={lw} height={lh} rx={6 * drawingScale}
                        fill={bgColor} stroke={borderColor} strokeWidth={2 * drawingScale} filter="url(#shadow)" />
                      <text x={midX + labelOffsetX} y={midY + labelOffsetY - 1 * drawingScale}
                        textAnchor="middle" fontSize={fs1} fill="white"
                        className="font-bold pointer-events-none">
                        {Math.round(length)}mm
                      </text>
                      <text x={midX + labelOffsetX} y={midY + labelOffsetY + fs2}
                        textAnchor="middle" fontSize={fs2} fill="rgba(255, 255, 255, 0.9)"
                        className="font-medium pointer-events-none">
                        {Math.round(direction)}°{isSnapped ? ' ⊙' : ''}
                      </text>
                    </g>
                  );
                })()}
              </g>
            )}
            
            {points.length > 1 && (() => {
              const epX = points[points.length - 1].x;
              const epY = points[points.length - 1].y;
              const epScale = drawingScale;
              const outerR = 16 * epScale;
              const innerR = 12 * epScale;
              const plusSize = 7 * epScale;
              const plusStroke = 2.5 * epScale;
              const dashLen = 4 * epScale;
              const labelFontSize = Math.max(9, Math.min(13, 11 * epScale));
              const labelOffset = (outerR + 10 * epScale);
              return (
              <g>
                {/* Dashed outer attention ring */}
                <circle
                  cx={epX} cy={epY} r={outerR}
                  fill="none" stroke="#3b82f6" strokeWidth={2 * epScale}
                  strokeDasharray={`${dashLen},${dashLen}`}
                  opacity="0.6"
                  className="pointer-events-none"
                >
                  <animate attributeName="stroke-dashoffset" from="0" to={dashLen * 4} dur="1.5s" repeatCount="indefinite" />
                </circle>
                {/* Solid inner circle */}
                <circle
                  cx={epX} cy={epY} r={innerR}
                  fill="#3b82f6" stroke="#2563eb" strokeWidth={2 * epScale}
                  className="cursor-pointer transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDrawStart({ x: epX, y: epY });
                    setDrawCurrent(null);
                    setDrawingMode('line');
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDrawStart({ x: epX, y: epY });
                    setDrawCurrent(null);
                    setDrawingMode('line');
                  }}
                  style={{ pointerEvents: 'all' }}
                />
                {/* Plus symbol inside circle */}
                <line x1={epX - plusSize} y1={epY} x2={epX + plusSize} y2={epY}
                  stroke="white" strokeWidth={plusStroke} strokeLinecap="round" className="pointer-events-none" />
                <line x1={epX} y1={epY - plusSize} x2={epX} y2={epY + plusSize}
                  stroke="white" strokeWidth={plusStroke} strokeLinecap="round" className="pointer-events-none" />
                {/* "Continue drawing" label */}
                <text
                  x={epX} y={epY - labelOffset}
                  textAnchor="middle" fontSize={labelFontSize}
                  fill="#2563eb"
                  className="font-semibold pointer-events-none"
                >
                  Continue drawing
                </text>
              </g>
              );
            })()}
        </svg>
      </div>
    );
  };

  const handleSave = () => {
    const config: FlashingConfig = {
      segments,
      variation,
      plastisolPrice,
      polyesterPrice,
      galvanisedSteelPrice,
      quantity,
      weltStart,
      weltEnd,
      type: flashingType,
      color,
      gauge
    };
    onSave(config);
  };

  // Configuration Dialog Content wrapper with ref to get values
  const configurationFormRef = useRef<FlashingConfigurationFormRef>(null);
  
  // Stable component that doesn't re-render unnecessarily
  const ConfigurationContent = React.useCallback(() => {
    return (
      <FlashingConfigurationForm
        ref={configurationFormRef}
        initialVariation={variation}
        initialQuantity={quantity}
        initialWeltStart={weltStart}
        initialWeltEnd={weltEnd}
        initialPlastisolPrice={plastisolPrice}
        initialPolyesterPrice={polyesterPrice}
        initialGalvanisedSteelPrice={galvanisedSteelPrice}
        initialType={flashingType}
        initialColor={color}
        initialGauge={gauge}
        totalWidth={totalWidth}
        segmentsLength={segments.length}
        readOnlyPrices={readOnlyPrices}
        onSave={() => {
          // This won't be called anymore - we'll read values via ref
        }}
      />
    );
  }, [variation, quantity, weltStart, weltEnd, plastisolPrice, polyesterPrice, flashingType, color, gauge, totalWidth, segments.length, readOnlyPrices]);

  // Shared sidebar content for desktop sidebar and mobile sheet
  const renderSidebarContent = () => (
    <div className="space-y-6">
      {/* Segments List */}
      <section>
        <p className="text-xs text-muted-foreground mb-3">
          Total width: <span className="font-semibold">{totalWidth}mm</span>
        </p>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {segments.map((segment, index) => {
            const isSelected = selectedSegmentId === segment.id;
            return (
              <div
                key={segment.id}
                className={`p-3 rounded-lg border transition-colors ${
                  isSelected
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                    : 'bg-card hover:bg-muted border-border'
                }`}
                onClick={() => setSelectedSegmentId(segment.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge variant={segment.type === 'line' ? 'default' : 'secondary'} className="text-xs">
                        {segment.type === 'line' ? 'Line' : 'Bend'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">#{index + 1}</span>
                    </div>
                    {segment.type === 'line' && segment.length !== undefined && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Length:</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditDialog(segment.id, 'length'); }}
                            className="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                          >
                            {segment.length.toFixed(0)}mm
                          </button>
                        </div>
                        {segment.direction !== undefined && (
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Direction:</span>
                            <span>{Math.round(segment.direction)}°</span>
                          </div>
                        )}
                      </div>
                    )}
                    {segment.type === 'bend' && segment.angle !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Angle:</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditDialog(segment.id, 'angle'); }}
                          className="text-amber-600 dark:text-amber-400 hover:underline font-semibold"
                        >
                          {(() => {
                            const normalized = normalizeAngle(segment.angle);
                            const absOriginal = Math.abs(segment.angle);
                            const displayAngle = (normalized === 45 && absOriginal === 135) ||
                                              (normalized === 45 && absOriginal > 90) ? 135 : normalized;
                            return Math.round(displayAngle);
                          })()}°
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (segment.type === 'line') openEditDialog(segment.id, 'length');
                        else openEditDialog(segment.id, 'angle');
                      }} title="Edit">
                      <Menu className="w-3.5 h-3.5" />
                    </Button>
                    {segments.length > 1 && (
                      <Button type="button" variant="ghost" size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); removeSegment(segment.id); }} title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {segments.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No segments yet.<br />Start drawing to add segments.
            </div>
          )}
        </div>
      </section>

      {/* Configuration */}
      <section className="border-t pt-4">
        <Label className="text-base font-semibold">Configuration</Label>
        <div className="space-y-3 mt-2">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Type:</span><span className="font-semibold capitalize">{flashingType}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Variation:</span><span className="font-semibold capitalize">{variation}</span></div>
          {color && (<div className="flex justify-between text-sm"><span className="text-muted-foreground">Colour:</span><span className="font-semibold">{color}</span></div>)}
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Gauge:</span><span className="font-semibold">{gauge}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Quantity:</span><span className="font-semibold">{quantity}</span></div>
          <div className="space-y-2 border-t pt-3">
            <Label className="text-sm font-semibold">Welt Options</Label>
            <div className="space-y-1">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Welt at Start:</span><span className="font-semibold">{weltStart ? 'Yes (+15mm)' : 'No'}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Welt at End:</span><span className="font-semibold">{weltEnd ? 'Yes (+15mm)' : 'No'}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t pt-4">
        <Label className="text-base font-semibold">Pricing</Label>
        <div className="space-y-2 mt-2">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Plastisol (per sheet):</span><span className="font-semibold">£{plastisolPrice.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span></span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Polyester (per sheet):</span><span className="font-semibold">£{polyesterPrice.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span></span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Galvanised Steel (per sheet):</span><span className="font-semibold">£{galvanisedSteelPrice.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span></span></div>
        </div>
      </section>

      {/* Calculations */}
      <section className="border-t pt-4">
        <Label className="text-base font-semibold">Calculations</Label>
        <div className="bg-muted/50 rounded-lg p-4 space-y-3 mt-2">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Width:</span><span className="font-semibold">{totalWidth}mm</span></div>
          {(weltStart || weltEnd) && (
            <div className="text-xs text-muted-foreground pl-2 border-l-2 border-primary/20 space-y-1">
              <div className="flex justify-between"><span>Base width:</span><span>{totalWidth - (weltStart ? 15 : 0) - (weltEnd ? 15 : 0)}mm</span></div>
              {weltStart && (<div className="flex justify-between"><span>Welt at start:</span><span>+15mm</span></div>)}
              {weltEnd && (<div className="flex justify-between"><span>Welt at end:</span><span>+15mm</span></div>)}
            </div>
          )}
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Sheet Size:</span><span className="font-semibold">{SHEET_WIDTH}mm × {SHEET_LENGTH}mm</span></div>
          {totalWidth > 0 && (
            <>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Units per Sheet:</span><span className="font-semibold">{unitsPerSheet}</span></div>
              {offcutPerSheet > 0 && (<div className="flex justify-between text-sm"><span className="text-muted-foreground">Offcut per Sheet:</span><span className="font-semibold text-orange-600">{offcutPerSheet.toFixed(1)}mm</span></div>)}
              <div className="text-xs text-muted-foreground italic pl-2 border-l-2 border-primary/20">
                Calculation: {SHEET_WIDTH}mm ÷ {totalWidth}mm = {unitsPerSheet} units{offcutPerSheet > 0 && ` (${offcutPerSheet.toFixed(1)}mm offcut)`}
              </div>
            </>
          )}
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Variation:</span><span className="font-semibold capitalize">{variation}</span></div>
          {unitsPerSheet > 0 && (
            <>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Price per Unit:</span><span className="font-semibold">£{pricePerUnit.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span></span></div>
              <div className="text-xs text-muted-foreground italic pl-2 border-l-2 border-primary/20">
                Calculation: £{(() => { let p = 0; if (variation === 'plastisol') p = plastisolPrice; else if (variation === 'polyester') p = polyesterPrice; else if (variation === 'galvanised_steel') p = galvanisedSteelPrice; return p.toFixed(2); })()} ÷ {unitsPerSheet} = £{pricePerUnit.toFixed(2)} (ex VAT)
              </div>
            </>
          )}
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Quantity:</span><span className="font-semibold">{quantity}</span></div>
          <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
            <span>Total Price:</span>
            <span className="text-primary">£{calculatePrice().toFixed(2)} <span className="text-sm font-normal text-muted-foreground">ex VAT</span></span>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div ref={builderRootRef} className="w-full h-full flex flex-col"
      style={{ touchAction: 'none', overscrollBehavior: 'contain' }}>
      {/* Main Content: Canvas and Sidebar — fills full height */}
      <div className="flex-1 overflow-hidden flex" style={{ minHeight: 0 }}>
        {/* Canvas Area - Shrinks when sidebar is visible */}
        <div
          ref={containerRef}
          className="flex-shrink-0 overflow-hidden bg-muted/30 relative transition-all duration-300"
          style={{
            width: isMobileDevice ? '100%' : 'calc(100% - 320px)',
            height: '100%',
            position: 'relative',
            zIndex: 1,
            pointerEvents: showConfiguration ? 'none' : 'auto',
            touchAction: 'none',
            overscrollBehavior: 'contain',
          }}
          onWheel={showConfiguration ? undefined : handleWheel}
        >
          {renderSVG()}

          {/* Drawing Controls - Floating */}
          <div 
            className="absolute top-2 left-2 z-10 flex flex-col gap-1" 
            style={{ pointerEvents: showConfiguration ? 'none' : 'auto' }}
          >
            {/* Quick Start Templates — hide on mobile when segments exist */}
            {(!isMobileDevice || segments.length === 0) && <div className="flex flex-col gap-1.5 bg-background/95 backdrop-blur-sm rounded-xl p-2 shadow-lg border">
              <div className="text-xs font-semibold text-muted-foreground px-0.5">Quick Start</div>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyTemplate('ridge', 135, 100)}
                  className="h-9 px-3 text-xs font-medium"
                  title="Ridge 135° - 100mm"
                >
                  Ridge 135°
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyTemplate('barge', 90, 100)}
                  className="h-9 px-3 text-xs font-medium"
                  title="Barge 90° - 100mm"
                >
                  Barge 90°
                </Button>
              </div>
            </div>}

            {/* Drawing Controls */}
            <div className="flex gap-1.5 bg-background/95 backdrop-blur-sm rounded-xl p-2 shadow-lg border">
              <Button
                type="button"
                variant={drawingMode === 'line' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  if (drawingMode === 'line') {
                    cancelDrawing();
                  } else {
                    setDrawingMode('line');
                    setIsContinuousDrawing(true);
                    setDrawStart(null);
                    setDrawCurrent(null);
                    setDrawStartPoint(null);
                  }
                }}
                className={`h-9 px-3 text-xs font-medium ${drawingMode === 'line' ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {drawingMode === 'line' ? 'Drawing...' : 'Draw Line'}
              </Button>
              {/* Auto/Manual toggle — desktop only */}
              {!isMobileDevice && drawingMode === 'line' && (
                <Button
                  type="button"
                  variant={isContinuousDrawing ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setIsContinuousDrawing(!isContinuousDrawing);
                    if (!isContinuousDrawing && segments.length > 0) {
                      const pathEnd = getPathEndPoint();
                      setDrawStart({ x: pathEnd.x, y: pathEnd.y });
                    }
                  }}
                  className="h-9 px-3 text-xs font-medium"
                  title={isContinuousDrawing ? 'Lines auto-connect' : 'Draw independent lines'}
                >
                  {isContinuousDrawing ? 'Auto' : 'Manual'}
                </Button>
              )}
              <Button type="button" variant="ghost" size="sm" onClick={handleUndo}
                disabled={undoStack.length === 0}
                className={`${isMobileDevice ? 'h-11 w-11' : 'h-9 w-9'} p-0`} title="Undo">
                <Undo2 className={`w-4 h-4 ${undoStack.length === 0 ? 'opacity-30' : ''}`} />
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleRedo}
                disabled={redoStack.length === 0}
                className={`${isMobileDevice ? 'h-11 w-11' : 'h-9 w-9'} p-0`} title="Redo">
                <Redo2 className={`w-4 h-4 ${redoStack.length === 0 ? 'opacity-30' : ''}`} />
              </Button>
              {/* Snap Toggle */}
              <Button type="button" variant={snappingEnabled ? 'default' : 'outline'} size="sm"
                onClick={() => setSnappingEnabled(!snappingEnabled)}
                className={`${isMobileDevice ? 'h-11 px-2' : 'h-9 px-2'} text-[10px] font-medium ${snappingEnabled ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                title={snappingEnabled ? 'Snapping ON' : 'Snapping OFF'}>
                {snappingEnabled ? '⊙ Snap' : '⊙ Snap'}
              </Button>
            </div>

            {/* Status Indicators */}
            {drawingMode === 'line' && (
              <div className={`bg-blue-600 backdrop-blur-sm rounded-xl px-3 py-2 text-white shadow-lg border border-blue-700 flex items-center gap-2 ${isMobileDevice ? 'text-xs' : 'text-sm'}`}>
                <span className="w-2 h-2 rounded-full bg-white animate-pulse flex-shrink-0" />
                <span className="font-medium">Drawing</span>
                {!isMobileDevice && (
                  <span className="text-blue-200 text-xs">
                    Click & drag · Hold Shift for 45° snap · ESC to exit
                  </span>
                )}
                {snappingEnabled && (
                  <span className="bg-green-500/30 text-green-200 px-1.5 py-0.5 rounded text-[10px] font-medium">SNAP</span>
                )}
              </div>
            )}
            {!isMobileDevice && drawingMode !== 'line' && segments.length === 0 && (
              <div className="bg-emerald-600/95 backdrop-blur-sm rounded-xl px-3 py-2 text-sm text-white shadow-lg border border-emerald-700">
                <span className="font-medium">Get started:</span>{' '}
                <span className="text-emerald-100 text-xs">Click "Draw Line" or choose a template</span>
              </div>
            )}
            {!isMobileDevice && drawingMode !== 'line' && segments.length > 0 && (
              <div className="bg-gray-600/90 backdrop-blur-sm rounded-xl px-3 py-1.5 text-xs text-white shadow-lg border border-gray-700">
                Right-click drag to pan · Scroll to zoom · Click labels to edit
              </div>
            )}
          </div>

          {/* Floating Controls - Top Right Corner - Compact and Organized */}
          <div 
            className="absolute top-2 right-2 z-10 flex flex-col gap-2" 
            style={{ pointerEvents: showConfiguration ? 'none' : 'auto' }}
          >
            {/* Zoom Controls */}
            <div className="flex items-center gap-1.5 bg-background/95 backdrop-blur-sm rounded-xl p-1.5 shadow-lg border">
              <Button type="button" variant="ghost" size="sm" onClick={handleZoomIn}
                className={`${isMobileDevice ? 'h-10 w-10' : 'h-8 w-8'} p-0`} title="Zoom In">
                <ZoomIn className="w-4 h-4" />
              </Button>
              {!isMobileDevice && (
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground min-w-[3.5rem] text-center">
                  {Math.round((1000 / viewBox.width) * 100)}%
                </div>
              )}
              <Button type="button" variant="ghost" size="sm" onClick={handleZoomOut}
                className={`${isMobileDevice ? 'h-10 w-10' : 'h-8 w-8'} p-0`} title="Zoom Out">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleFitToView}
                className={`${isMobileDevice ? 'h-10 w-10' : 'h-8 w-8'} p-0`} title="Fit to View">
                <Maximize2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Total Width Info — desktop only (mobile has bottom bar) */}
            {!isMobileDevice && segments.length > 0 && (
              <div className="bg-background/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-lg border">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-foreground">{totalWidth}mm</span>
                </div>
              </div>
            )}

            {/* Action Buttons — desktop only (mobile has bottom bar) */}
            {!isMobileDevice && (
              <div className="flex items-center gap-1.5 bg-background/95 backdrop-blur-sm rounded-xl p-1.5 shadow-lg border">
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setShowConfiguration(true)} className="h-8 px-3 text-xs font-medium">
                  Configure & Price
                </Button>
                <Button type="button" size="sm" onClick={handleSave}
                  className="h-8 px-3 text-xs font-medium">
                  Save Flashing
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Calculation Sidebar — desktop only */}
        {!isMobileDevice && <div
          className="w-[320px] flex-shrink-0 border-l bg-background flex flex-col shadow-lg"
          style={{ height: '100%' }}
          data-sidebar="true"
        >
          <div className="px-4 pt-4 pb-3 flex-shrink-0 border-b">
            <h2 className="text-base font-semibold">Calculations</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Configuration and pricing
            </p>
          </div>

          <div
            className="flex-1 overflow-y-auto px-4 py-3"
            style={{ minHeight: 0, maxHeight: '100%', touchAction: 'pan-y' }}
            onWheel={(e) => { e.stopPropagation(); }}
          >
            {renderSidebarContent()}
          </div>

          <div className="px-4 py-3 border-t flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              size="sm"
              onClick={() => setShowConfiguration(true)}
            >
              Edit Configuration
            </Button>
          </div>
        </div>}
      </div>

      {/* Mobile Bottom Bar */}
      {isMobileDevice && (
        <div className="flex-shrink-0 border-t bg-background px-3 py-2 flex items-center gap-2"
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">
              {segments.length > 0 ? `${totalWidth}mm` : 'No segments'}
              {segments.length > 0 && unitsPerSheet > 0 && (
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  £{calculatePrice().toFixed(2)}
                </span>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setMobileSheetOpen(true)}
            className="h-10 px-3 text-xs font-medium flex-shrink-0"
          >
            <ChevronUp className="w-4 h-4 mr-1" />
            Details
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowConfiguration(true)}
            className="h-10 px-3 text-xs font-medium flex-shrink-0"
          >
            Config
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            className="h-10 px-3 text-xs font-medium flex-shrink-0"
          >
            Save
          </Button>
        </div>
      )}

      {/* Mobile Bottom Sheet */}
      {isMobileDevice && (
        <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
          <SheetContent side="bottom" className="h-[70vh] flex flex-col p-0">
            <SheetHeader className="px-4 pt-4 pb-3 flex-shrink-0 border-b">
              <SheetTitle className="text-base">Calculations</SheetTitle>
              <SheetDescription className="text-xs">Configuration and pricing</SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4 py-3" style={{ minHeight: 0, touchAction: 'pan-y' }}>
              {renderSidebarContent()}
            </div>
            <div className="px-4 py-3 border-t flex-shrink-0">
              <Button
                type="button"
                variant="outline"
                className="w-full h-11"
                size="sm"
                onClick={() => { setMobileSheetOpen(false); setShowConfiguration(true); }}
              >
                Edit Configuration
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Configuration Dialog */}
      <Dialog open={showConfiguration} onOpenChange={setShowConfiguration} modal={true}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] p-0 flex flex-col"
          onOpenAutoFocus={(e) => {
            // Prevent auto-focus completely
            e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // Prevent focus trap
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            // Prevent closing when clicking on select dropdowns or form elements
            const target = e.target as HTMLElement;
            if (
              target.closest('[data-radix-select-content]') || 
              target.closest('[data-radix-popper-content-wrapper]') ||
              target.closest('input') ||
              target.closest('select') ||
              target.closest('textarea') ||
              target.closest('button')
            ) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            // Allow escape to close
          }}
          onPointerDownOutside={(e) => {
            // Prevent closing when clicking form elements
            const target = e.target as HTMLElement;
            if (
              target.closest('input') ||
              target.closest('select') ||
              target.closest('textarea') ||
              target.closest('[data-radix-select-content]') ||
              target.closest('[data-radix-popper-content-wrapper]')
            ) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
            <DialogTitle>Configure Flashing & Pricing</DialogTitle>
            <DialogDescription>
              Set variation, quantity, welt options, and pricing to calculate the total cost.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            <ConfigurationContent />
          </div>
          
          <div className="px-6 py-4 border-t flex justify-end gap-2 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfiguration(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                // Get values from form ref before closing
                if (configurationFormRef.current) {
                  const values = configurationFormRef.current.getValues();
                  setVariation(values.variation);
                  setQuantity(values.quantity);
                  setWeltStart(values.weltStart);
                  setWeltEnd(values.weltEnd);
                  setPlastisolPrice(values.plastisolPrice);
                  setPolyesterPrice(values.polyesterPrice);
                  setGalvanisedSteelPrice(values.galvanisedSteelPrice);
                  setFlashingType(values.type);
                  setColor(values.color || '');
                  setGauge(values.gauge || '0.5mm');
                }
                setShowConfiguration(false);
                // Don't call handleSave() - user can save the flashing separately using the main "Save Flashing" button
              }}
            >
              Save & Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editingSegment !== null} onOpenChange={(open) => {
        if (!open) {
          setEditingSegment(null);
          setEditingField(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingField === 'length' ? 'Edit Length' : editingField === 'angle' ? 'Edit Angle' : 'Edit Segment'}
            </DialogTitle>
            <DialogDescription>
              {editingField === 'length' ? 'Enter the length in millimeters' : editingField === 'angle' ? 'Enter the angle in degrees (-180 to 180)' : ''}
            </DialogDescription>
          </DialogHeader>
          {editingField === 'length' && editingSegment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Length (mm)</Label>
                <Input
                  type="number"
                  value={tempLength}
                  onChange={(e) => setTempLength(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveLength(editingSegment);
                    } else if (e.key === 'Escape') {
                      setEditingSegment(null);
                      setEditingField(null);
                      setTempLength('');
                    }
                  }}
                  autoFocus
                  min="0"
                  step="0.1"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingSegment(null);
                    setEditingField(null);
                    setTempLength('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSaveLength(editingSegment)}
                >
                  Save
                </Button>
              </div>
          </div>
        )}
          {editingField === 'angle' && editingSegment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Angle (degrees)</Label>
                <Input
                  type="number"
                  value={tempAngle}
                  onChange={(e) => setTempAngle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveAngle(editingSegment);
                    } else if (e.key === 'Escape') {
                      setEditingSegment(null);
                      setEditingField(null);
                      setTempAngle('');
                    }
                  }}
                  autoFocus
                  min="-180"
                  max="180"
                  step="1"
                />
                <p className="text-xs text-muted-foreground">
                  For ridge flashings, use 135° (external angle). For other angles, use the internal angle.
                </p>
      </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingSegment(null);
                    setEditingField(null);
                    setTempAngle('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSaveAngle(editingSegment)}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
